/**
 * JourneySettings - Configure journey framework settings
 * Shows phases, activities, artifacts, and allows goal selection
 */

import { useEffect, useState } from 'react'
import { useJourneyStore } from '@/stores/journeyStore'
import { useAuthStore } from '@/stores/authStore'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Loader2,
  Target,
  CheckCircle2,
  Circle,
  FileText,
  Box,
  ChevronDown,
  ChevronRight,
  SkipForward,
  Sprout,
  Search,
  Map as MapIcon,
  Rocket,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PhaseMode } from '@ispot/shared'

// Goal ID to icon mapping
const goalIcons: Record<string, React.ReactNode> = {
  'validate-new-idea': <Sprout className="w-5 h-5" />,
  'diagnose-stalled-growth': <Search className="w-5 h-5" />,
  'sharpen-positioning': <Target className="w-5 h-5" />,
  'prioritize-roadmap': <MapIcon className="w-5 h-5" />,
  'expand-new-market': <Rocket className="w-5 h-5" />,
}

// Mode badge colors
const modeColors: Record<PhaseMode, { bg: string; text: string; label: string }> = {
  full: { bg: 'bg-market/20', text: 'text-market', label: 'Full' },
  check: { bg: 'bg-business/20', text: 'text-business', label: 'Check' },
  review: { bg: 'bg-solution/20', text: 'text-solution', label: 'Review' },
  skip: { bg: 'bg-warm-200', text: 'text-text-muted', label: 'Skip' },
}

export function JourneySettings() {
  const { currentAccount, currentProject } = useAuthStore()
  const {
    framework,
    journey,
    isLoading,
    isUpdating,
    fetchFramework,
    fetchJourney,
    selectGoal,
    getSelectedGoal,
  } = useJourneyStore()

  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set())

  // Fetch framework on mount (account-level, no project needed)
  useEffect(() => {
    if (currentAccount?.id) {
      fetchFramework(currentAccount.id)
    }
  }, [currentAccount?.id, fetchFramework])

  // Fetch project journey if a project is selected
  useEffect(() => {
    if (currentAccount?.id && currentProject?.id) {
      fetchJourney(currentAccount.id, currentProject.id)
    }
  }, [currentAccount?.id, currentProject?.id, fetchJourney])

  const togglePhase = (phaseId: string) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev)
      if (next.has(phaseId)) {
        next.delete(phaseId)
      } else {
        next.add(phaseId)
      }
      return next
    })
  }

  const handleGoalSelect = async (goalId: string) => {
    if (currentAccount?.id && currentProject?.id) {
      await selectGoal(currentAccount.id, currentProject.id, goalId)
    }
  }

  if (!currentAccount) {
    return (
      <div className="text-center py-12 text-text-muted">
        <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>Please select an account to view journey settings</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-business" />
      </div>
    )
  }

  if (!framework) {
    return (
      <div className="text-center py-12 text-text-muted">
        <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>Loading journey framework...</p>
        <p className="text-sm mt-2">If this persists, the framework may not be initialized.</p>
      </div>
    )
  }

  const selectedGoal = getSelectedGoal()

  return (
    <div className="space-y-6">
      {/* Framework Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-business" />
            {framework.name}
          </CardTitle>
          <CardDescription>{framework.description}</CardDescription>
        </CardHeader>
      </Card>

      {/* Goal Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Journey Goal</CardTitle>
          <CardDescription>Select your discovery goal to customize your journey path</CardDescription>
        </CardHeader>
        <CardContent>
          {!currentProject ? (
            <div className="text-center py-6 text-text-muted border border-dashed border-warm-200 rounded-lg">
              <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Select a project to configure its journey goal</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {framework.goals.map((goal) => {
                const isSelected = journey?.goalId === goal.id
                const phaseCount = Object.values(goal.phaseConfig).filter(mode => mode !== 'skip').length
                return (
                  <button
                    key={goal.id}
                    onClick={() => handleGoalSelect(goal.id)}
                    disabled={isUpdating}
                    className={cn(
                      'p-4 rounded-lg border text-left transition-all',
                      isSelected
                        ? 'border-business bg-business/5 ring-2 ring-business/20'
                        : 'border-warm-200 hover:border-warm-300 hover:bg-warm-50'
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className={cn(
                            'w-8 h-8 rounded-lg flex items-center justify-center',
                            isSelected ? 'bg-business/20 text-business' : 'bg-warm-100 text-text-secondary'
                          )}>
                            {goalIcons[goal.id] || <Target className="w-4 h-4" />}
                          </div>
                          <span className="font-medium text-warm-900">{goal.name}</span>
                        </div>
                        <div className="text-sm text-text-muted mb-2">
                          {goal.shortDescription || goal.description}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Badge
                            variant="outline"
                            className={cn(
                              goal.type === 'greenfield' ? 'text-market' : 'text-business'
                            )}
                          >
                            {goal.type === 'greenfield' ? 'New Product' : 'Existing Product'}
                          </Badge>
                          <Badge variant="outline" className="text-text-muted">
                            {phaseCount} phases
                          </Badge>
                        </div>
                        {goal.recommendedFor && goal.recommendedFor.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {goal.recommendedFor.slice(0, 3).map((useCase, i) => (
                              <span
                                key={i}
                                className="text-xs px-2 py-0.5 rounded bg-warm-100 text-text-muted"
                              >
                                {useCase}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      {isSelected && <CheckCircle2 className="h-5 w-5 text-business flex-shrink-0" />}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Phases Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Journey Phases</CardTitle>
          <CardDescription>
            {selectedGoal
              ? `${Object.values(selectedGoal.phaseConfig).filter(m => m !== 'skip').length} phases included for "${selectedGoal.name}"`
              : `${framework.phases.length} phases in this journey`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {framework.phases.map((phase) => {
            const isExpanded = expandedPhases.has(phase.id)
            const progress = journey?.phaseProgress[phase.id] || 0
            const phaseMode = selectedGoal?.phaseConfig[phase.id] as PhaseMode | undefined
            const isSkipped = phaseMode === 'skip'
            const modeStyle = phaseMode ? modeColors[phaseMode] : modeColors.full

            return (
              <div
                key={phase.id}
                className={cn(
                  'border border-warm-200 rounded-lg overflow-hidden',
                  isSkipped && 'opacity-50'
                )}
              >
                {/* Phase Header */}
                <button
                  onClick={() => togglePhase(phase.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-warm-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium',
                        isSkipped && 'opacity-50'
                      )}
                      style={{ backgroundColor: phase.color }}
                    >
                      {isSkipped ? <SkipForward className="h-4 w-4" /> : phase.order}
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <span className={cn('font-medium text-warm-900', isSkipped && 'line-through')}>
                          {phase.name}
                        </span>
                        {phaseMode && phaseMode !== 'full' && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${modeStyle.bg} ${modeStyle.text}`}>
                            {modeStyle.label}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-text-muted">{phase.objective}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {!isSkipped && (
                      <div className="text-right">
                        <div className="text-sm font-medium text-warm-900">{Math.round(progress)}%</div>
                        <div className="text-xs text-text-muted">complete</div>
                      </div>
                    )}
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-text-muted" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-text-muted" />
                    )}
                  </div>
                </button>

                {/* Phase Details */}
                {isExpanded && (
                  <div className="border-t border-warm-200 p-4 bg-warm-50/50">
                    <p className="text-sm text-text-secondary mb-4">{phase.description}</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Activities */}
                      <div>
                        <h4 className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2">
                          Activities ({phase.activities.length})
                        </h4>
                        <div className="space-y-2">
                          {phase.activities.map((activity) => {
                            const isCompleted = journey?.completedActivities.includes(activity.id)
                            return (
                              <div
                                key={activity.id}
                                className="flex items-start gap-2 text-sm"
                              >
                                {isCompleted ? (
                                  <CheckCircle2 className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                                ) : (
                                  <Circle className="h-4 w-4 text-text-muted flex-shrink-0 mt-0.5" />
                                )}
                                <span className={cn(isCompleted && 'text-text-muted line-through')}>
                                  {activity.name}
                                  {activity.isRequired && (
                                    <span className="text-error ml-1">*</span>
                                  )}
                                </span>
                              </div>
                            )
                          })}
                        </div>
                      </div>

                      {/* Artifacts */}
                      <div>
                        <h4 className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2">
                          Artifacts ({phase.artifacts.length})
                        </h4>
                        <div className="space-y-2">
                          {phase.artifacts.map((artifact) => {
                            const isCompleted = journey?.completedArtifacts.includes(artifact.id)
                            const Icon = artifact.type === 'canvas' ? Box : FileText
                            return (
                              <div
                                key={artifact.id}
                                className="flex items-start gap-2 text-sm"
                              >
                                <Icon className={cn(
                                  'h-4 w-4 flex-shrink-0 mt-0.5',
                                  isCompleted ? 'text-success' : 'text-text-muted'
                                )} />
                                <span className={cn(isCompleted && 'text-text-muted line-through')}>
                                  {artifact.name}
                                  {artifact.isRequired && (
                                    <span className="text-error ml-1">*</span>
                                  )}
                                </span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Gate */}
                    {phase.gate && (
                      <div className="mt-4 pt-4 border-t border-warm-200">
                        <h4 className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2">
                          Gate: {phase.gate.name}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {phase.gate.criteria.map((criterion, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {criterion}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </CardContent>
      </Card>

      {/* Required Entities Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Entity Requirements</CardTitle>
          <CardDescription>
            Entities required for phase completion across the journey
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['market', 'solution', 'business'].map((domain) => {
              const domainPhases = framework.phases.filter((p) => p.domain === domain)
              const domainEntities = domainPhases.flatMap((p) => p.entities)
              const uniqueEntities = [...new Map(domainEntities.map((e) => [e.classUri, e])).values()]

              return (
                <div key={domain} className="p-4 rounded-lg bg-warm-50 border border-warm-100">
                  <h4
                    className="font-medium capitalize mb-3"
                    style={{
                      color:
                        domain === 'market'
                          ? 'var(--color-market)'
                          : domain === 'solution'
                            ? 'var(--color-solution)'
                            : 'var(--color-business)',
                    }}
                  >
                    {domain}
                  </h4>
                  <div className="space-y-1.5">
                    {uniqueEntities.slice(0, 5).map((entity) => (
                      <div key={entity.classUri} className="text-sm flex items-center justify-between">
                        <span className="text-text-secondary">{entity.label}</span>
                        <span className="text-xs text-text-muted">
                          min: {entity.minCount}
                        </span>
                      </div>
                    ))}
                    {uniqueEntities.length > 5 && (
                      <div className="text-xs text-text-muted pt-1">
                        +{uniqueEntities.length - 5} more
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default JourneySettings
